import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

type Politeness = 'polite' | 'assertive';
type Announce = (message: string, politeness?: Politeness) => void;

const LiveRegionContext = createContext<Announce>(() => {});

/** Announce a status change to screen readers (spec §2, status announcements). */
export const useAnnounce = () => useContext(LiveRegionContext);

/**
 * Two visually hidden live regions, one polite (role=status) and one
 * assertive (role=alert). Repeating a message clears the region first so
 * screen readers read it again.
 */
export const LiveRegionProvider = ({ children }: { children: ReactNode }) => {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const announce = useCallback<Announce>((message, politeness = 'polite') => {
    const set = politeness === 'assertive' ? setAssertive : setPolite;
    set('');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => set(message), 50);
  }, []);

  return (
    <LiveRegionContext.Provider value={announce}>
      {children}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        data-testid="live-polite"
      >
        {polite}
      </div>
      <div
        className="sr-only"
        role="alert"
        aria-live="assertive"
        data-testid="live-assertive"
      >
        {assertive}
      </div>
    </LiveRegionContext.Provider>
  );
};
